import React from 'react';

interface AvatarProps {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  bgColor?: string;
  textColor?: string;
  className?: string;
}

/**
 * Avatar - Reusable avatar component for user initials
 * 
 * Displays user initials in a circular avatar with customizable colors and sizes.
 * Falls back to the first character of the name if name is provided.
 * 
 * @param name - The user's name to extract initials from
 * @param size - The size of the avatar (sm, md, lg)
 * @param bgColor - Background color class
 * @param textColor - Text color class
 * @param className - Additional CSS classes
 */
const Avatar: React.FC<AvatarProps> = ({ 
  name, 
  size = 'md', 
  bgColor = 'bg-orange-100', 
  textColor = 'text-orange-600',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-lg'
  };

  const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <div className={`${sizeClasses[size]} ${bgColor} rounded-full flex items-center justify-center font-medium ${className}`}>
      <span className={textColor}>
        {getInitials(name)}
      </span>
    </div>
  );
};

export default Avatar;
